import { cx } from '@orn/shared-web/utils/cx';
import { Link } from 'react-router-dom';

import InspectLinkedText from '../../components/InspectLinkedText';

import { MemoryBodyText } from './AssistantMemoryBody';
import { journalThreadLinkPath } from './assistantMemoryJournalView';

import type { AdminListAssistantMemoryJournalQuery } from './__generated__/AssistantMemoryJournal.gql';

export type JournalEntry = AdminListAssistantMemoryJournalQuery['adminListAssistantMemoryJournal']['entries'][number];

const OPERATION_BADGE: Record<string, string> = {
  record: 'bg-bg-secondary text-fg-success-primary border-border-primary',
  update: 'bg-bg-secondary text-fg-brand-primary border-border-brand',
  forget: 'bg-bg-secondary text-fg-error-primary border-border-primary',
};

interface IImageProps {
  label: string;
  image: JournalEntry['before'];
}

const ImageBlock = ({ label, image }: IImageProps) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    <div className="text-fg-quaternary text-[10px] uppercase tracking-wider">{label}</div>
    {image === null || image === undefined ? (
      <div className="text-fg-quaternary text-[11px] italic">—</div>
    ) : (
      <div className="border-border-primary flex flex-col gap-1 border px-2 py-1.5">
        <div className="text-fg-primary whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">
          <MemoryBodyText body={image.body} />
        </div>
        <div className="text-fg-tertiary font-mono text-[10px]">
          imp {image.importance}
          {image.tags.length > 0 && ` · ${image.tags.join(', ')}`}
        </div>
      </div>
    )}
  </div>
);

interface IProps {
  entries: JournalEntry[];
  loading: boolean;
  /** True when a scope held more entries than the per-scope cap, so this is a partial history. */
  hasMore: boolean;
  /** Set when the read failed; rendered instead of the list. */
  error?: Error;
  /** The open pane's member and household; the source-thread link falls back to them. */
  threadLinkContext: { userId: string | null; familyId: string };
}

/**
 * The journaled writes behind one memory row, newest first. Shows both images
 * on every entry rather than only the changed side: a `forget`'s `before` is
 * the only surviving copy of what the row held, and an `update` reads as a
 * diff only with both halves in view.
 */
const AssistantMemoryJournalList = ({ entries, loading, hasMore, error, threadLinkContext }: IProps) => {
  if (loading) {
    return <div className="text-fg-secondary text-xs">Loading history…</div>;
  }
  // Distinct from the empty state on purpose: an empty list after a failed read
  // would read as "this row has no history", which is the one wrong answer.
  if (error) {
    return <div className="text-fg-error-primary text-xs">Could not load this row's history: {error.message}</div>;
  }
  if (entries.length === 0) {
    return (
      <div className="text-fg-tertiary text-xs italic">
        No journaled writes for this memory — it predates the journal.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {hasMore && (
        <div className="border-border-primary text-fg-secondary border-l-2 pl-3 text-[11px] italic">
          Showing the most recent entries only — this row's history is longer than the read limit.
        </div>
      )}
      {entries.map((entry) => (
        <div key={entry.id} className="border-border-primary flex flex-col gap-2 border-l-2 pl-3">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cx(
                'inline-flex items-center border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                OPERATION_BADGE[entry.operation] ?? 'bg-bg-secondary text-fg-primary border-border-primary'
              )}
            >
              {entry.operation}
            </span>
            <span className="text-fg-secondary font-mono text-[11px]">{entry.actor}</span>
            <span className="text-fg-tertiary font-mono text-[11px]">{entry.scope}</span>
            <span
              className="text-fg-tertiary font-mono text-[11px]"
              title={`${new Date(entry.createdAt).toISOString()} (UTC)`}
            >
              {new Date(entry.createdAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })}
            </span>
            {entry.sourceThreadId && (
              <Link
                to={journalThreadLinkPath(
                  { sourceThreadId: entry.sourceThreadId, actorUserId: entry.actorUserId },
                  threadLinkContext
                )}
                className="text-fg-brand-primary hover:text-fg-brand-secondary font-mono text-[11px] underline-offset-2 hover:underline"
                title={entry.sourceThreadId}
              >
                {entry.sourceThreadId.slice(0, 8)}
              </Link>
            )}
          </div>
          {entry.rationale && (
            <div className="text-fg-secondary text-[11px] italic">
              <InspectLinkedText text={entry.rationale} />
            </div>
          )}
          <div className="flex flex-row gap-2">
            <ImageBlock label="Before" image={entry.before} />
            <ImageBlock label="After" image={entry.after} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default AssistantMemoryJournalList;
